/**
 * カラーツール
 */

/**
 * カラーの輝度を調整する
 * @param hex hexカラー
 * @returns カラー
 */
function getLuminance(hex: string): number {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  const { r, g, b } = result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : { r: 0, g: 0, b: 0 };
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

/**
 * カラーの明るさを調整する
 * @param hex hexカラー
 * @param percent パーセント
 * @returns カラー
 */
function adjustBrightness(hex: string, percent: number): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  const { r, g, b } = result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : { r: 0, g: 0, b: 0 };

  const adjust = (value: number) => {
    const adjusted = value + (255 - value) * percent;
    return Math.min(255, Math.max(0, Math.round(adjusted)));
  };

  const adjustedR = adjust(r);
  const adjustedG = adjust(g);
  const adjustedB = adjust(b);

  return (
    "#" +
    ((1 << 24) + (adjustedR << 16) + (adjustedG << 8) + adjustedB)
      .toString(16)
      .slice(1)
  );
}

/**
 * ホバーカラーを取得する
 * @param baseColor ベースカラー
 * @param explicitColor 指定されたホバーカラー
 * @returns ホバーカラー
 */
export function getHoverColor(
  baseColor: string,
  explicitColor?: string
): string {
  // もう指定された場合、変換は不要
  if (explicitColor && explicitColor !== baseColor) {
    return explicitColor;
  }

  // 透明色の場合
  if (baseColor === "transparent" || baseColor === "rgba(0,0,0,0)") {
    return "rgba(0, 0, 0, 0.05)";
  }
  // 上記以外
  const luminance = getLuminance(baseColor);
  return luminance > 0.5
    ? adjustBrightness(baseColor, -0.1)
    : adjustBrightness(baseColor, 0.1);
}

/**
 * アクティブカラーを取得する
 * @param baseColor ベースカラー
 * @returns アクティブカラー
 */
export function getActiveColor(baseColor: string): string {
  // 透明色の場合
  if (baseColor === "transparent" || baseColor === "rgba(0,0,0,0)") {
    return "rgba(0, 0, 0, 0.1)";
  }
  // 上記以外
  const luminance = getLuminance(baseColor);
  return luminance > 0.5
    ? adjustBrightness(baseColor, -0.15)
    : adjustBrightness(baseColor, 0.15);
}
